import React from "react";
import { Link } from "react-router-dom";
import { Gem, Sparkles, HandHeart, ShieldCheck } from "lucide-react";
import { SEO } from "@/components/common/SEO";
import { Reveal } from "@/components/common/Reveal";
import { SectionHeading } from "@/components/common/SectionHeading";
import { useImagery } from "@/hooks/useCatalog";
import { brand } from "@/config/brand";

const VALUES = [
  { icon: Sparkles, title: "Our Philosophy", text: "We believe jewellery should feel personal — an extension of who you are, not just an accessory. Every ÉLORA piece is designed to be worn, loved and passed on." },
  { icon: HandHeart, title: "Crafted with Care", text: "From the first sketch to the final polish, each design passes through the hands of master artisans who have honed their craft over generations." },
  { icon: ShieldCheck, title: "Quality Promise", text: "Only hallmarked metals and ethically sourced, certified stones. Every purchase is backed by our lifetime craftsmanship guarantee." },
];

export default function AboutPage() {
  const { data } = useImagery();

  return (
    <div>
      <SEO title="Our Story" description={`Discover the story behind ${brand.name} — craftsmanship, philosophy and a promise of timeless quality.`} />

      {/* Hero */}
      <section className="relative h-[52vh] min-h-[360px] w-full overflow-hidden">
        <img src={data?.about_story} alt="ÉLORA craftsmanship" className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-ink/45" />
        <div className="relative mx-auto flex h-full max-w-3xl flex-col items-center justify-center px-4 text-center text-ivory">
          <span className="text-luxe text-xs font-medium text-gold">Est. 2026 · Mumbai</span>
          <h1 className="mt-4 font-serif text-4xl font-light sm:text-5xl lg:text-6xl">The ÉLORA Story</h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-ivory/85 sm:text-base">
            {brand.tagline}
          </p>
        </div>
      </section>

      {/* Brand story */}
      <section className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:py-24">
        <Reveal>
          <SectionHeading eyebrow="Who We Are" title="Jewellery for life's moments" />
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground">
            ÉLORA began with a simple idea — that fine jewellery should be as meaningful as it is beautiful. Founded by a small collective of designers and goldsmiths, we set out to create pieces that celebrate the everyday and the extraordinary alike. Today, each ÉLORA creation is handcrafted in limited quantities, using time-honoured techniques and the finest materials, so that every piece carries a story worth telling.
          </p>
        </Reveal>
      </section>

      {/* Values */}
      <section className="bg-cream py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="flex flex-col items-center px-4 text-center">
                  <span className="grid h-14 w-14 place-items-center rounded-full bg-white text-gold shadow-sm">
                    <v.icon size={24} strokeWidth={1.4} />
                  </span>
                  <h3 className="mt-5 font-serif text-2xl text-ink">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Craftsmanship */}
      <section id="craftsmanship" className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:py-24">
        <Reveal>
          <div className="aspect-[4/5] overflow-hidden bg-cream">
            <img src={data?.about_craft} alt="Master goldsmith at work" className="h-full w-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <span className="text-luxe text-xs font-medium text-gold">The Atelier</span>
          <h2 className="mt-4 font-serif text-3xl font-light text-ink sm:text-4xl lg:text-5xl">A devotion to craftsmanship</h2>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground">
            In our atelier, tradition meets precision. Each piece is hand-finished, stone by stone, by artisans who treat every commission as a work of art. We embrace slow, considered making — because true luxury cannot be rushed.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-ink">
            {["Hand-selected, certified gemstones", "Hallmarked 18k & 22k gold", "Lifetime craftsmanship guarantee"].map((t) => (
              <li key={t} className="flex items-center gap-3"><Gem size={15} className="text-gold" /> {t}</li>
            ))}
          </ul>
          <Link to="/shop" className="mt-8 inline-block bg-ink px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-gold">
            Shop The Collection
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
